import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import RiskGauge from "../components/sepsis/RiskGauge.jsx";
import { useT } from "../i18n/LanguageContext.jsx";
import { secondaryRiskLabel } from "../i18n/strings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SimulatorPage() {
  const [config, setConfig] = useState(null);
  const [examples, setExamples] = useState([]);
  const [values, setValues] = useState({});
  const [patientRef, setPatientRef] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { t } = useT();

  const groups = useMemo(
    () => [
      { title: t("sim.group.neutrophils"), keys: ["neu_X", "neu_Y", "neu_Z", "neu_"] },
      { title: t("sim.group.lymphocytes"), keys: ["lin_X", "lin_Y", "lin_Z", "lin_"] },
      { title: t("sim.group.monocytes"), keys: ["mon_X", "mon_Y", "mon_Z", "mon_"] },
      { title: t("sim.group.rdw"), keys: ["rdw_cv", "rdw_sd"] },
      { title: t("sim.group.ratios"), keys: ["NLR__9_path_", "MLR", "PLR"] },
      { title: t("sim.group.platelets"), keys: ["plt", "pct", "pdw"] },
    ],
    [t]
  );

  useEffect(() => {
    (async () => {
      try {
        const [c, ex] = await Promise.all([
          axios.get(`${API}/config`),
          axios.get(`${API}/examples`),
        ]);
        setConfig(c.data);
        setExamples(ex.data || []);
        const initial = {};
        (c.data.variables || []).forEach((v) => {
          initial[v.key] = v.default_value;
        });
        setValues(initial);
      } catch (e) {
        setError(String(e.message || e));
      }
    })();
  }, []);

  const variableMap = useMemo(() => {
    const m = {};
    (config?.variables || []).forEach((v) => {
      m[v.key] = v;
    });
    return m;
  }, [config]);

  const setField = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));

  const loadExample = (ex) => {
    setValues({ ...(values || {}), ...(ex.values || {}) });
    setResult(null);
  };

  const calculate = async () => {
    setLoading(true);
    setError(null);
    try {
      const cleaned = {};
      Object.entries(values).forEach(([k, v]) => {
        if (v === "" || v === null || v === undefined) return;
        cleaned[k] = Number(v);
      });
      const r = await axios.post(`${API}/calculate`, {
        patient: cleaned,
        patient_reference: patientRef.trim() || null,
      });
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || String(e.message || e));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    const initial = {};
    (config?.variables || []).forEach((v) => {
      initial[v.key] = v.default_value;
    });
    setValues(initial);
    setPatientRef("");
    setResult(null);
  };

  if (!config) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 py-12" data-testid="simulator-loading">
        <div className="data-label">{t("cfg.loading")}</div>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10" data-testid="simulator-page">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
        <div>
          <div className="data-label mb-2">{t("sim.kicker")}</div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
            {t("sim.title")}
          </h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">{t("sim.description")}</p>
        </div>
        <div className="flex flex-col items-end gap-3">
          <div className="flex items-center gap-3" data-testid="sponsor-simulator">
            <div className="data-label">{t("sim.sponsoredBy")}</div>
            <img
              src="https://customer-assets.emergentagent.com/job_sepsis-index-api/artifacts/5vxfurvb_StarHealth_horizontal.jpeg"
              alt="StarHealth"
              className="h-9 w-auto object-contain"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2 justify-end">
            {examples.map((ex) => (
              <button
                key={ex.name}
                onClick={() => loadExample(ex)}
                data-testid={`preset-${ex.name.replace(/\s+/g, "-").toLowerCase()}`}
                className="px-3 py-2 text-xs font-mono border border-slate-300 hover:bg-slate-50"
              >
                {ex.name}
              </button>
            ))}
            <button
              onClick={reset}
              data-testid="btn-reset-inputs"
              className="px-3 py-2 text-xs font-mono border border-slate-300 hover:bg-slate-50"
            >
              {t("sim.reset")}
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-10">
        <div>
          <div className="border border-slate-200 p-5 mb-6 bg-slate-50/60" data-testid="patient-ref-block">
            <label className="block">
              <div className="data-label mb-2">{t("sim.patientRef.label")}</div>
              <input
                type="text"
                value={patientRef}
                onChange={(e) => setPatientRef(e.target.value)}
                placeholder={t("sim.patientRef.placeholder")}
                maxLength={128}
                data-testid="input-patient-reference"
                className="w-full font-mono text-sm px-3 py-2 border border-slate-300 focus:border-slate-900 outline-none bg-white"
              />
              <div className="text-[11px] text-slate-500 mt-2 leading-snug">
                {t("sim.patientRef.help")}
              </div>
            </label>
          </div>

          <div className="border border-slate-200" data-testid="inputs-panel">
            {groups.map((group, gi) => (
              <div key={group.title} className={gi > 0 ? "border-t border-slate-200" : ""}>
                <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
                  <div className="data-label">{group.title}</div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-px bg-slate-200">
                  {group.keys.map((k) => {
                    const spec = variableMap[k];
                    if (!spec) return null;
                    return (
                      <label key={k} className="bg-white p-4 flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                          <span className="data-label">{k}</span>
                        </div>
                        <input
                          type="number"
                          step="any"
                          data-testid={`input-${k}`}
                          value={values[k] ?? ""}
                          onChange={(e) => setField(k, e.target.value)}
                          className="font-mono tabular-nums text-base px-3 py-2 border border-slate-300 focus:border-slate-900 focus:ring-0 outline-none bg-white"
                        />
                        <div className="text-[11px] text-slate-500 leading-snug">
                          {spec.label}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-3 mt-6">
            <button
              onClick={calculate}
              disabled={loading}
              data-testid="btn-calculate-risk"
              className="px-6 py-3 bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-50 border border-slate-900"
            >
              {loading ? t("sim.calculating") : t("sim.calculate")}
            </button>
            {error && (
              <div
                className="text-sm text-red-700 border border-red-300 bg-red-50 px-3 py-2"
                data-testid="calc-error"
              >
                {error}
              </div>
            )}
          </div>
        </div>

        <div className="lg:sticky lg:top-24 self-start" data-testid="result-panel">
          {!result ? (
            <div className="border border-slate-200 p-10 text-center clinical-grid">
              <div className="data-label mb-3">{t("sim.awaiting.title")}</div>
              <div className="text-sm text-slate-500 max-w-xs mx-auto">{t("sim.awaiting.body")}</div>
            </div>
          ) : (
            <ResultView result={result} t={t} />
          )}
        </div>
      </div>
    </div>
  );
}

function ResultView({ result, t }) {
  const { lang } = useT();
  const combined = result.combined;
  const txId = result.transaction_id;
  const [copied, setCopied] = useState(false);
  const methods = [
    { key: "lda", title: t("sim.method.lda"), data: result.lda },
    { key: "tree", title: t("sim.method.tree"), data: result.decision_tree },
    { key: "combined", title: t("sim.method.combined"), data: result.combined },
  ];
  const classLabels = result.class_labels || [];

  const copyTxId = async () => {
    if (!txId) return;
    try {
      await navigator.clipboard.writeText(txId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard may be blocked; surface no-op
    }
  };

  return (
    <div className="space-y-6 rise-in">
      <div className="border border-slate-200 p-6">
        <div className="data-label mb-3">{t("sim.result.combined")}</div>
        <RiskGauge
          probability={combined.probability_sepsis}
          riskLabel={`${combined.risk_class_en} · ${secondaryRiskLabel(combined, lang)}`}
        />
        {txId && (
          <div className="mt-5 pt-4 border-t border-slate-200" data-testid="result-tx-id-block">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="data-label mb-1">{t("sim.result.txId")}</div>
                <div className="font-mono text-[12px] text-slate-900 break-all" data-testid="result-tx-id">
                  {txId}
                </div>
              </div>
              <button
                type="button"
                onClick={copyTxId}
                data-testid="btn-copy-tx-id"
                className="shrink-0 px-3 py-1.5 text-[11px] font-mono border border-slate-300 hover:bg-slate-50 text-slate-700"
              >
                {copied ? t("sim.result.copied") : t("sim.result.copy")}
              </button>
            </div>
            <p className="text-[11px] text-slate-500 mt-2">{t("sim.result.txIdHint")}</p>
            {result.patient_reference && (
              <div className="mt-3 flex items-baseline justify-between gap-3" data-testid="result-patient-ref-block">
                <span className="data-label">{t("sim.result.patientRef")}</span>
                <span className="font-mono text-[12px] text-slate-900 break-all text-right" data-testid="result-patient-ref">
                  {result.patient_reference}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="border border-slate-200" data-testid="methodology-compare">
        <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 data-label">
          {t("sim.result.compare")}
        </div>
        <div className="divide-y divide-slate-200">
          {methods.map((m) => (
            <div
              key={m.key}
              className="px-5 py-4 flex items-center justify-between"
              data-testid={`method-row-${m.key}`}
            >
              <div>
                <div className="text-sm font-medium text-slate-900">{m.title}</div>
                <div className="text-[11px] text-slate-500 font-mono mt-1">
                  {m.data.risk_class_en} · {secondaryRiskLabel(m.data, lang)}
                </div>
              </div>
              <div className="font-mono tabular-nums text-2xl text-slate-900">
                {m.data.probability_sepsis.toFixed(4)}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="border border-slate-200" data-testid="per-class-probs">
        <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 data-label">
          {t("sim.result.perClass")}
        </div>
        <div>
          {combined.per_class_probabilities.map((p, i) => (
            <div key={i} className="px-5 py-3 border-b last:border-b-0 border-slate-100">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-800">{classLabels[i] || `class ${i}`}</span>
                <span className="font-mono tabular-nums text-slate-900">{p.toFixed(4)}</span>
              </div>
              <div className="mt-2 h-1.5 bg-slate-100">
                <div
                  className="h-full"
                  style={{
                    width: `${Math.max(0, Math.min(1, p)) * 100}%`,
                    backgroundColor: i === 0 ? "#10B981" : "#0055FF",
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {result.decision_tree.details?.matched_node && (
        <div className="border border-slate-200" data-testid="tree-path">
          <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 data-label">
            {t("sim.result.path")}{" "}
            <span className="text-slate-900">
              {result.decision_tree.details.matched_node}
            </span>
          </div>
          <ul className="p-5 space-y-1.5 font-mono text-[12px] text-slate-700">
            {(result.decision_tree.details.path_conditions || []).map((c, i) => (
              <li key={i}>
                <span className="text-slate-500 mr-2">{String(i + 1).padStart(2, "0")}</span>
                {c.variable} {c.operator} {c.threshold}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
